import React, { FC, useEffect, useState, lazy, Suspense, ComponentType } from "react";
import { createBrowserRouter, Navigate, Outlet, RouterProvider} from "react-router-dom";
import ROUTES from '../../library/constants/routeConstants';
import { attachAuthInterceptor } from "../axios/checkmateApi";
import { useAppDispatch, useAppSelector } from "../hooks/hooks";
import { selectAuthToken, selectIsAdmin, callGetLoggedInUser } from "../slices/authSlice";
import PrivateRoute from "./PrivateRoute";
import { canAccessAdminRoutes } from "./routeRules";
import { Box, CircularProgress } from "@mui/material";

const lazyRetry = function(componentImport: () => Promise<{ default: ComponentType<any> }>): Promise<{ default: ComponentType<any> }> {
    return new Promise((resolve, reject) => {
        const hasRefreshed = JSON.parse(
            window.sessionStorage.getItem('retry-lazy-refreshed') || 'false'
        );
        componentImport().then((component) => {
            window.sessionStorage.setItem('retry-lazy-refreshed', 'false'); 
            resolve(component);
        }).catch((error) => {
            if (!hasRefreshed) {
                window.sessionStorage.setItem('retry-lazy-refreshed', 'true'); 
                return window.location.reload(); 
            }
            reject(error); // default error behavior as already tried refresh
        });
    });
};


const Login = lazy(() => lazyRetry(() => import('../../pages/Login/Login')));
const Register = lazy(() => lazyRetry(() => import('../../pages/Register/Register')));
const Forgot = lazy(() => lazyRetry(() => import('../../pages/Forgot/Forgot')));
const SearchHistory = lazy(() => lazyRetry(() => import("../../pages/SearchHistory/SearchHistory")));
const QueryBuilder = lazy(() => lazyRetry(() => import("../../pages/QueryBuilder/QueryBuilder")));
const DetailPage = lazy(() => lazyRetry(() => import("../../pages/DetailPage/DetailPage")));
const EntitySummary = lazy(() => lazyRetry(() => import("../../pages/EntitySummary/EntitySummary")));
const GraphView = lazy(() => lazyRetry(() => import("../../pages/GraphView/GraphView")));
const UserList = lazy(() => lazyRetry(() => import("../../pages/UserList/UserList")));
const UserDetails = lazy(() => lazyRetry(() => import("../../pages/UserDetails/UserDetails")));
const TeamList = lazy(() => lazyRetry(() => import("../../pages/TeamList/TeamList")));
const TeamDetails = lazy(() => lazyRetry(() => import("../../pages/TeamDetails/TeamDetails")));
const RoleList = lazy(() => lazyRetry(() => import("../../pages/RoleList/RoleList")));
const ReportList = lazy(() => lazyRetry(() => import('../../pages/ReportList/ReportList')))
const ReportWizard = lazy(() => lazyRetry(() => import('../../pages/ReportWizard/ReportWizard')))
const PasswordReset = lazy(() => lazyRetry(() => import('../../pages/PasswordReset/PasswordReset')))
const SettingsPage = lazy(() => lazyRetry(() => import('../../pages/SettingsPage/SettingsPage')))
const NotFoundPage = lazy(() => lazyRetry(() => import('../../pages/NotFoundPage/NotFoundPage')))
const UploadPage = lazy(() => lazyRetry(() => import('../../pages/UploadPage/UploadPage')))
const Dashboard = lazy(() => lazyRetry(() => import('../../pages/Dashboard/Dashboard')))
const ATAI = lazy(() => lazyRetry(() => import('../../pages/ATAI/ATAI')))
const MultipleTargets = lazy(() => lazyRetry(() => import('../../pages/MultipleTargets/MultipleTargets')))
const AuthCallback = lazy(() => lazyRetry(() => import('../../pages/AuthCallback/AuthCallback')))
const TenantList = lazy(() => lazyRetry(() => import('../../pages/TenantList/TenantList')))
const TenantDetails = lazy(() => lazyRetry(() => import('../../pages/TenantDetails/TenantDetails')))
const DataSourceList = lazy(() => lazyRetry(() => import('../../pages/DataSourceList/DataSourceList')))
const StatusPage = lazy(() => lazyRetry(() => import('../../pages/StatusPage/StatusPage')))

const publicFallback = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress />
    </Box>
);

const AppRouter: FC = () => {
    const dispatch = useAppDispatch();

    const {
        authToken,
        isAdmin
    } = useAppSelector((state) => ({
        authToken: selectAuthToken(state),
        isAdmin: selectIsAdmin(state)
    }));

    const [renderApp, setRenderApp] = useState(false);
    const [userDataLoaded, setUserDataLoaded] = useState(false);
    const [router, setRouter] = useState<any>(null);

    useEffect(() => {
        if (authToken) {
            attachAuthInterceptor(authToken);
            dispatch(callGetLoggedInUser()).then(() => {
                setRenderApp(true);
                setUserDataLoaded(true);
            });
        } else {
            setRenderApp(true);
            setUserDataLoaded(true);
        }
    }, [authToken, dispatch]);

    useEffect(() => {
        if (renderApp && userDataLoaded) {
            setRouter(getRouterDef());
        }
    }, [renderApp, userDataLoaded, isAdmin]);

    const getRouterDef = () => createBrowserRouter([
        { path: ROUTES.LOGIN, element:
            <Suspense fallback={publicFallback}>
                <Login />
            </Suspense>
        },
        { path: ROUTES.REGISTER, element:
            <Suspense fallback={publicFallback}>
                <Register />
            </Suspense>
        },
        { path: ROUTES.FORGOT, element:
            <Suspense fallback={publicFallback}>
                <Forgot />
            </Suspense>
        },
        { path: ROUTES.RESET_PW, element:
            <Suspense fallback={publicFallback}>
                <PasswordReset />
            </Suspense>
        },
        { path: ROUTES.AUTH_CALLBACK, element:
            <Suspense fallback={publicFallback}>
                <AuthCallback />
            </Suspense>
        },
        {
            path: ROUTES.DASHBOARD,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <Dashboard />
            </PrivateRoute>
        },
        { 
            path: ROUTES.REPORTS,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <ReportList />
            </PrivateRoute>
        },
        { 
            path: ROUTES.REPORT_WIZARD,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <ReportWizard />
            </PrivateRoute>,
        },
        { 
            path: ROUTES.QUERY_PAGE,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <QueryBuilder />
            </PrivateRoute>
        },{ 
            path: ROUTES.QUERY_PAGE_RESULT,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <QueryBuilder />
            </PrivateRoute>
        },{ 
            path: ROUTES.OVERVIEW,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <DetailPage />
            </PrivateRoute>
        },{ 
            path: ROUTES.SUMMARY,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <EntitySummary />
            </PrivateRoute>
        },{ 
            path: ROUTES.GRAPH,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <GraphView />
            </PrivateRoute>
        },{ 
            path: ROUTES.USERS,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={canAccessAdminRoutes(isAdmin)}>
                <UserList />
            </PrivateRoute>
        },{ 
            path: ROUTES.USER_DETAILS,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={canAccessAdminRoutes(isAdmin)}>
                <UserDetails />
            </PrivateRoute>
        },{ 
            path: ROUTES.TEAMS,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={canAccessAdminRoutes(isAdmin)}>
                <TeamList />
            </PrivateRoute>
        },{ 
            path: ROUTES.TEAM_DETAILS,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={canAccessAdminRoutes(isAdmin)}>
                <TeamDetails />
            </PrivateRoute>
        },
        { 
            path: ROUTES.TENANTS,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={canAccessAdminRoutes(isAdmin)}>
                <TenantList />
            </PrivateRoute>
        },
        { 
            path: ROUTES.TENANT_DETAILS,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={canAccessAdminRoutes(isAdmin)}>
                <TenantDetails />
            </PrivateRoute>
        },
        { 
            path: ROUTES.DATA_SOURCES,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={canAccessAdminRoutes(isAdmin)}>
                <DataSourceList />
            </PrivateRoute>
        },
        { 
            path: ROUTES.ROLES,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={canAccessAdminRoutes(isAdmin)}>
                <RoleList />
            </PrivateRoute>
        },{ 
            path: ROUTES.SETTINGS,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <SettingsPage />
            </PrivateRoute>
        },
        {
            path: ROUTES.HISTORY,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <SearchHistory />
            </PrivateRoute>
        },
        { path: '/history', element: <Navigate to={ROUTES.HISTORY} replace /> },
        { 
            path: ROUTES.UPLOAD,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <UploadPage />
            </PrivateRoute>
        },
        { 
            path: ROUTES.ATAI,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <ATAI />
            </PrivateRoute>
        },
        { 
            path: ROUTES.MULTIPLE_TARGETS,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <MultipleTargets />
            </PrivateRoute>
        },
        {
            path: ROUTES.STATUS,
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <StatusPage />
            </PrivateRoute>
        },
        {
            path: "*",
            element: <PrivateRoute renderApp={renderApp} canAccessRoute={true}>
                <NotFoundPage />
            </PrivateRoute>
        }
    ])

    if (!router) {
        return null;
    }

    return <>
        <RouterProvider router={router} />
    </>
}

export default AppRouter;